import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
};

const stagger = {
  container: {
    hidden: { opacity: 0 },
    show: { opacity: 1, transition: { staggerChildren: 0.15 } },
  },
  item: {
    hidden: { opacity: 0, y: 30 },
    show: { opacity: 1, y: 0 },
  },
};

export default function ProductsPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addToCart } = useCart();

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}/products`);
        setProducts(response.data);
      } catch (error) {
        console.error("Failed to fetch products:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const handleAddToCart = (product) => {
    addToCart(product, 100, 1);
    toast.success(`${product.name} sepete eklendi`);
  };

  return (
    <div className="pt-24">
      {/* Hero Section */}
      <section className="py-16 bg-[#0D3B2E]" data-testid="products-hero">
        <div className="section-container">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="caption-text text-[#C5A059] mb-4 block">
              Koleksiyonumuz
            </span>
            <h1 className="heading-xl text-[#F9F7F2]">Ürünlerimiz</h1>
            <p className="body-text text-[#F9F7F2]/70 mt-4 max-w-xl">
              Geleneksel ustalıkla hazırlanan premium Türk kahvesi
              koleksiyonumuzu keşfedin.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="section-spacing bg-[#F9F7F2]" data-testid="products-grid">
        <div className="section-container">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="spinner" />
            </div>
          ) : (
            <motion.div
              variants={stagger.container}
              initial="hidden"
              animate="show"
              className="grid grid-cols-1 md:grid-cols-2 gap-12"
            >
              {products.map((product) => (
                <motion.div
                  key={product.id}
                  variants={stagger.item}
                  className="group"
                  data-testid={`product-item-${product.slug}`}
                >
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Image */}
                    <Link
                      to={`/urunler/${product.slug}`}
                      className="relative overflow-hidden"
                    >
                      <img
                        src={product.image_url}
                        alt={product.name}
                        className="w-full aspect-square object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-[#0D3B2E]/0 group-hover:bg-[#0D3B2E]/10 transition-all duration-500" />
                    </Link>

                    {/* Content */}
                    <div className="flex flex-col justify-center">
                      <span className="caption-text text-[#C5A059] mb-2">
                        {product.roast_level}
                      </span>
                      <Link to={`/urunler/${product.slug}`}>
                        <h2 className="heading-md text-[#0D3B2E] mb-3 group-hover:text-[#C5A059] transition-colors">
                          {product.name}
                        </h2>
                      </Link>
                      <p className="body-text text-[#5D4037] mb-4 text-sm">
                        {product.short_description}
                      </p>

                      {/* Aroma Notes */}
                      <div className="mb-4">
                        <span className="caption-text text-[#5D4037]/70 text-[10px] mb-2 block">
                          Aroma Notları
                        </span>
                        <div className="flex flex-wrap gap-2">
                          {product.aroma_notes.map((note, i) => (
                            <span
                              key={i}
                              className="text-xs px-2 py-1 bg-[#E5E0D8] text-[#5D4037]"
                            >
                              {note}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Price & Actions */}
                      <div className="flex items-center gap-4 mt-auto">
                        <span className="font-display text-2xl text-[#0D3B2E]">
                          ₺{product.price.toFixed(2)}
                        </span>
                        <span className="text-sm text-[#5D4037]/60">
                          / 100g
                        </span>
                      </div>

                      <div className="flex gap-3 mt-4">
                        <button
                          onClick={() => handleAddToCart(product)}
                          className="btn-primary flex-1"
                          data-testid={`add-to-cart-${product.slug}`}
                        >
                          Sepete Ekle
                        </button>
                        <Link
                          to={`/urunler/${product.slug}`}
                          className="btn-outline"
                          data-testid={`view-details-${product.slug}`}
                        >
                          Detay
                        </Link>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </section>

      {/* Wholesale CTA */}
      <section className="py-20 bg-[#0D3B2E]" data-testid="wholesale-cta">
        <div className="section-container">
          <motion.div {...fadeUp} className="text-center">
            <h2 className="heading-lg text-[#F9F7F2] mb-4">
              Toptan Alım İçin
            </h2>
            <p className="body-text text-[#F9F7F2]/70 mb-8 max-w-xl mx-auto">
              Cafe, otel veya işletmeniz için özel fiyatlar ve numune talebi.
            </p>
            <Link
              to="/toptan-satis"
              className="btn-gold inline-block"
              data-testid="wholesale-cta-button"
            >
              Teklif Alın
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
